import React, { useState, useEffect, useCallback } from 'react';
import {
  AlertTriangle, AlertCircle, Info, Flame, CheckCircle, Clock, Eye,
  RefreshCw, Filter, ChevronDown, ChevronUp, XCircle, Check, RotateCcw,
  Cpu, HardDrive, Server, Database, Network, Shield
} from 'lucide-react';
import { aiAgent } from '../../../services/api/client';
import { toast } from '../../../utils/toastUtils';
import { useI18n } from '../../../i18n';

const AIAgentIncidents = () => {
  const { t } = useI18n();
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [filters, setFilters] = useState({
    severity: '',
    status: '',
    category: ''
  });
  const [actionLoading, setActionLoading] = useState(null);

  const fetchIncidents = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        page_size: 10,
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
      };
      const data = await aiAgent.getIncidents(params);
      setIncidents(data.items || []);
      setTotalPages(data.total_pages || 1);
    } catch (err) {
      console.error('Failed to fetch incidents:', err);
      toast.error(t('aiAgent.loadIncidentsError'));
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => {
    fetchIncidents();
  }, [fetchIncidents]);

  const handleAcknowledge = async (incidentId) => {
    setActionLoading(incidentId);
    try {
      await aiAgent.acknowledgeIncident(incidentId);
      toast.success(t('aiAgent.incidentAcknowledged'));
      fetchIncidents();
    } catch (err) {
      toast.error(t('common.error'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleResolve = async (incidentId) => {
    setActionLoading(incidentId);
    try {
      await aiAgent.resolveIncident(incidentId);
      toast.success(t('aiAgent.incidentResolved'));
      fetchIncidents();
    } catch (err) {
      toast.error(t('common.error'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleRetryRecovery = async (incidentId) => {
    setActionLoading(incidentId);
    try {
      await aiAgent.retryRecovery(incidentId);
      toast.success(t('aiAgent.recoveryRelaunched'));
      fetchIncidents();
    } catch (err) {
      toast.error(t('common.error'));
    } finally {
      setActionLoading(null);
    }
  };

  // Severity badge
  const getSeverityBadge = (severity) => {
    const config = {
      info: { color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: Info },
      warning: { color: 'bg-orange-500/20 text-orange-400 border-orange-500/30', icon: AlertTriangle },
      critical: { color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: AlertCircle },
      emergency: { color: 'bg-red-700/20 text-red-300 border-red-700/30', icon: Flame },
    };
    const cfg = config[severity] || config.info;
    const Icon = cfg.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs font-medium ${cfg.color}`}>
        <Icon className="w-3 h-3" />
        {severity}
      </span>
    );
  };

  // Status badge
  const getStatusBadge = (status) => {
    const config = {
      open: { color: 'bg-red-500/20 text-red-400', text: 'Ouvert' },
      acknowledged: { color: 'bg-yellow-500/20 text-yellow-400', text: 'Acquitte' },
      investigating: { color: 'bg-blue-500/20 text-blue-400', text: 'Investigation' },
      recovering: { color: 'bg-orange-500/20 text-orange-400', text: 'Recovering' },
      resolved: { color: 'bg-green-500/20 text-green-400', text: 'Resolu' },
      closed: { color: 'bg-gray-500/20 text-gray-400', text: 'Ferme' },
    };
    const cfg = config[status] || config.open;
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${cfg.color}`}>
        {cfg.text}
      </span>
    );
  };

  // Category icon
  const getCategoryIcon = (category) => {
    const icons = {
      resource: Cpu,
      scan: Shield,
      security: AlertTriangle,
      service: Server,
      database: Database,
      network: Network,
      celery: Server,
      redis: Database,
      gpu: Cpu,
    };
    const Icon = icons[category] || AlertCircle;
    return <Icon className="w-4 h-4" />;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-orange-400" />
            {t('aiAgent.incidentsTitle')}
          </h3>
          <p className="text-gray-400 text-sm mt-1">{t('aiAgent.incidentsSubtitle')}</p>
        </div>
        <button
          onClick={fetchIncidents}
          disabled={loading}
          className="p-2 bg-brand-black-lighter rounded-lg text-gray-400 hover:text-brand-green"
        >
          <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 p-4 bg-brand-black rounded-xl border border-gray-800">
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('aiAgent.filterSeverity')}</label>
          <select
            value={filters.severity}
            onChange={(e) => setFilters(f => ({ ...f, severity: e.target.value }))}
            className="px-3 py-1.5 bg-brand-black-lighter border border-gray-700 rounded-lg text-white text-sm"
          >
            <option value="">{t('aiAgent.allSeverities')}</option>
            <option value="info">Info</option>
            <option value="warning">Warning</option>
            <option value="critical">Critical</option>
            <option value="emergency">Emergency</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('aiAgent.filterStatus')}</label>
          <select
            value={filters.status}
            onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}
            className="px-3 py-1.5 bg-brand-black-lighter border border-gray-700 rounded-lg text-white text-sm"
          >
            <option value="">{t('aiAgent.allStatuses')}</option>
            <option value="open">Open</option>
            <option value="acknowledged">Acknowledged</option>
            <option value="investigating">Investigating</option>
            <option value="recovering">Recovering</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">{t('aiAgent.filterCategory')}</label>
          <select
            value={filters.category}
            onChange={(e) => setFilters(f => ({ ...f, category: e.target.value }))}
            className="px-3 py-1.5 bg-brand-black-lighter border border-gray-700 rounded-lg text-white text-sm"
          >
            <option value="">{t('aiAgent.allCategories')}</option>
            <option value="resource">Resource</option>
            <option value="scan">Scan</option>
            <option value="security">Security</option>
            <option value="service">Service</option>
            <option value="database">Database</option>
            <option value="network">Network</option>
          </select>
        </div>
      </div>

      {/* Incidents List */}
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-green"></div>
        </div>
      ) : incidents.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-500/50" />
          <p>{t('aiAgent.noIncidents')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {incidents.map((incident) => (
            <div
              key={incident.id}
              className={`bg-brand-black rounded-xl border transition-all ${
                selectedIncident?.id === incident.id
                  ? 'border-brand-green/50'
                  : 'border-gray-800 hover:border-gray-700'
              }`}
            >
              {/* Incident Header */}
              <div
                className="p-4 cursor-pointer"
                onClick={() => setSelectedIncident(
                  selectedIncident?.id === incident.id ? null : incident
                )}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="text-gray-400">
                      {getCategoryIcon(incident.category)}
                    </div>
                    <div>
                      <h4 className="text-white font-medium">{incident.title}</h4>
                      <div className="flex items-center gap-2 mt-1">
                        {getSeverityBadge(incident.severity)}
                        {getStatusBadge(incident.status)}
                        <span className="text-xs text-gray-500">
                          {new Date(incident.detected_at).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {incident.auto_recoverable && (
                      <span className="text-xs text-blue-400 bg-blue-500/20 px-2 py-0.5 rounded">
                        Auto-recoverable
                      </span>
                    )}
                    {selectedIncident?.id === incident.id ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>
              </div>

              {/* Incident Details */}
              {selectedIncident?.id === incident.id && (
                <div className="px-4 pb-4 border-t border-gray-800 mt-2 pt-4">
                  {incident.description && (
                    <p className="text-gray-400 text-sm mb-4">{incident.description}</p>
                  )}

                  {/* Recovery Status */}
                  <div className="flex flex-wrap gap-4 mb-4">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500 text-sm">Recovery:</span>
                      {incident.recovery_attempted ? (
                        incident.recovery_success ? (
                          <span className="text-green-400 text-sm flex items-center gap-1">
                            <CheckCircle className="w-4 h-4" /> {t('common.success')}
                          </span>
                        ) : (
                          <span className="text-red-400 text-sm flex items-center gap-1">
                            <XCircle className="w-4 h-4" /> {t('common.error')}
                          </span>
                        )
                      ) : (
                        <span className="text-gray-400 text-sm">{t('common.none')}</span>
                      )}
                    </div>
                    {incident.alert_sent && (
                      <div className="flex items-center gap-1 text-yellow-400 text-sm">
                        <AlertCircle className="w-4 h-4" /> {t('common.warning')}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2">
                    {incident.status === 'open' && (
                      <button
                        onClick={() => handleAcknowledge(incident.id)}
                        disabled={actionLoading === incident.id}
                        className="flex items-center gap-1 px-3 py-1.5 bg-yellow-500/20 text-yellow-400 rounded-lg text-sm hover:bg-yellow-500/30"
                      >
                        <Eye className="w-4 h-4" />
                        {t('aiAgent.acknowledge')}
                      </button>
                    )}
                    {['open', 'acknowledged', 'investigating', 'recovering'].includes(incident.status) && (
                      <button
                        onClick={() => handleResolve(incident.id)}
                        disabled={actionLoading === incident.id}
                        className="flex items-center gap-1 px-3 py-1.5 bg-green-500/20 text-green-400 rounded-lg text-sm hover:bg-green-500/30"
                      >
                        <Check className="w-4 h-4" />
                        {t('aiAgent.resolve')}
                      </button>
                    )}
                    {incident.auto_recoverable && !incident.recovery_success && (
                      <button
                        onClick={() => handleRetryRecovery(incident.id)}
                        disabled={actionLoading === incident.id}
                        className="flex items-center gap-1 px-3 py-1.5 bg-blue-500/20 text-blue-400 rounded-lg text-sm hover:bg-blue-500/30"
                      >
                        <RotateCcw className="w-4 h-4" />
                        {t('aiAgent.retryRecovery')}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1 bg-brand-black-lighter text-gray-400 rounded-lg disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-gray-400">
            Page {page} / {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-3 py-1 bg-brand-black-lighter text-gray-400 rounded-lg disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default AIAgentIncidents;
